"use client";

import If from "@/components/common/If";
import { Heading } from "@/components/ui/Heading";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import US from "./US";
import { useFindARep } from "@/providers/FindARep";
import CA from "./CA";
import Int from "./Int";
import Image from "@/components/common/Image";
import { Phone, Mail } from "lucide-react";
import Link from "next/link";

const states = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "DC",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  AB: "Alberta",
  BC: "British Columbia",
  MB: "Manitoba",
  NL: "Newfoundland and Labrador",
  NS: "Nova Scotia",
  NT: "Northwest Territories",
  NU: "Nunavut",
  ON: "Ontario",
  PE: "Prince Edward Island",
  QC: "Quebec",
  SK: "Saskatchewan",
  YT: "Yukon",
};

const missingReps = {
  FL: "Florida",
  MS: "Mississippi",
  TN: "Tennessee",
};

export const UI = ({ reps = [] }) => {
  const searchParams = useSearchParams();
  const t = searchParams.get("t");
  const [territoryName, setTerritoryName] = useState(t || "US");
  const [fullName, setFullName] = useState("");
  const [availableReps, setAvailableReps] = useState([]);
  const [availableArc, setAvailableArc] = useState([]);
  const { selectedState } = useFindARep();

  useEffect(() => {
    if (!selectedState) return;
    const state = states[selectedState];
    setFullName(state);

    const filteredReps = reps.filter(
      (rep) =>
        (rep?.states?.includes(state) || rep?.states?.includes("All US")) &&
        rep?.role !== "Design"
    );
    const filteredArcs = reps.filter(
      (rep) =>
        (rep?.states?.includes(state) || rep?.states?.includes("All US")) &&
        rep?.role === "Design"
    );
    setAvailableReps(filteredReps);
    setAvailableArc(filteredArcs);
  }, [selectedState, reps]);

  return (
    <div className="stack gap-12 container mx-auto">
      <Heading bottomBorder>Find a Representative</Heading>

      <div className="flex gap-4">
        <button
          className={`${territoryName === "US" ? "bg-gold border-gold" : ""} transition-all ease-in-out duration-300 border border-black px-4 py-2`}
          onClick={() => {
            setTerritoryName("US");
          }}
        >
          United States
        </button>
        <button
          className={`${territoryName === "Canada" ? "bg-gold border-gold" : ""} transition-all ease-in-out duration-300 border border-black px-4 py-2`}
          onClick={() => {
            setTerritoryName("Canada");
          }}
        >
          Canada
        </button>
        {/* <button
          className={`${territoryName === "International" ? "bg-gold border-gold" : ""} transition-all ease-in-out duration-300 border border-black px-4 py-2`}
          onClick={() => {
            setTerritoryName("International");
          }}
        >
          International
        </button>*/}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="flex flex-col gap-4 md:col-span-5">
          <div className="flex flex-col gap-12">
            <If
              condition={
                availableReps.length === 0 && missingReps[selectedState]
              }
            >
              <h2 className="text-h3">{fullName}</h2>
              <div className="stack gap-6">
                <div>
                  <h3 className="text-3xl text-black">
                    Territory Sales Managers
                  </h3>
                  <p className="text-gray">For Distributor Inquiries</p>
                </div>
                <p>
                  Please contact the{" "}
                  <Link
                    href="mailto:southeast@forteopenings.com"
                    className="underline"
                  >
                    Southeast customer service team.
                  </Link>
                </p>
              </div>
            </If>
            <If condition={availableReps.length}>
              <h2 className="text-h3">{fullName}</h2>
              <div className="stack gap-6">
                <div>
                  <h3 className="text-3xl text-black">
                    Territory Sales Managers
                  </h3>
                  <p className="text-gray">For Distributor and Owner Inquiries</p>
                </div>
                <ul className="stack gap-8">
                  {availableReps.map((rep) => (
                    <li key={rep?.name} className="flex gap-8 items-center">
                      <div className="w-24 h-24 rounded-full flex items-center justify-center bg-gray overflow-hidden">
                        <If condition={rep?.image?.[0]?.cdn_url}>
                          <Image
                            src={rep?.image?.[0]?.cdn_url}
                            alt={rep?.name}
                            className="w-full h-full"
                            imgClassName="w-full h-full"
                          />
                        </If>
                      </div>
                      <div className="flex flex-col gap-1">
                        <h4 className="text-2xl text-black">{rep?.name}</h4>
                        <p className="text-gray">{rep?.jobTitle}</p>
                        <div className="flex gap-2 items-start flex-col">
                          <span className="text-sm text-gray flex gap-2 leading-none items-center">
                            <Phone /> {rep?.phoneNumber}
                          </span>
                          <a
                            href={`mailto:${rep?.email}`}
                            className="text-sm text-gray flex gap-2"
                          >
                            <Mail /> {rep?.email}
                          </a>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </If>
            <If condition={availableArc.length > 0}>
              <div className="stack gap-6">
                <div>
                  <h3 className="text-3xl text-black">
                    Architectural Managers
                  </h3>
                  <p className="text-gray">
                    For Architect and Contractor Inquiries
                  </p>
                </div>

                <ul className="stack gap-8">
                  {availableArc.map((rep) => (
                    <li key={rep?.name} className="flex gap-8 items-center">
                      <div className="w-24 h-24 rounded-full flex items-center justify-center bg-gray overflow-hidden">
                        <If condition={rep?.image?.[0]?.cdn_url}>
                          <Image
                            src={rep?.image?.[0]?.cdn_url}
                            alt={rep?.name}
                            className="w-full h-full"
                            imgClassName="w-full h-full"
                          />
                        </If>
                      </div>
                      <div className="flex flex-col gap-1">
                        <h4 className="text-2xl text-black">{rep?.name}</h4>
                        <p className="text-gray">{rep?.jobTitle}</p>
                        <div className="flex gap-2 items-start flex-col">
                          <span className="text-sm text-gray flex gap-2 leading-none items-center">
                            <Phone /> {rep?.phoneNumber}
                          </span>
                          <a
                            href={`mailto:${rep?.email}`}
                            className="text-sm text-gray flex gap-2 hover:text-gold"
                          >
                            <Mail /> {rep?.email}
                          </a>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </If>
          </div>
        </div>
        <div className="md:col-span-7 pr-2">
          <If condition={territoryName === "US"}>
            <US />
          </If>
          <If condition={territoryName === "Canada"}>
            <CA />
          </If>
          <If condition={territoryName === "International"}>
            <Int />
          </If>
        </div>
      </div>
    </div>
  );
};
